/**
 * Remote MCP servers an org adds to cubic, used by cubic's agents (PR chat,
 * /code). Shared by the registry, its settings API and the settings UI.
 */

import type {
  McpServerAuthType as DbMcpServerAuthType,
  McpServerOAuthRegistration as DbMcpServerOAuthRegistration,
} from '@/generated/prisma/enums';

import { baseUrl } from '@/lib/env';
import { INTEGRATION_TYPES } from '@/lib/integrations/settings-pages';

// The values of the Prisma enums of the same names, which the columns use.
export const MCP_SERVER_AUTH_TYPES = {
  NONE: 'none',
  API_KEY: 'api_key',
  OAUTH: 'oauth',
} as const satisfies Record<string, DbMcpServerAuthType>;

export type McpServerAuthType = (typeof MCP_SERVER_AUTH_TYPES)[keyof typeof MCP_SERVER_AUTH_TYPES];

/** A server name, unanchored, for patterns that embed one (`tool-names.ts`). */
export const MCP_SERVER_NAME_SOURCE = '[a-z][a-z0-9-]{0,31}';

/**
 * The name becomes OpenCode's server name and the prefix of every tool the
 * agents see, so it stays short and needs no sanitizing.
 */
export const MCP_SERVER_NAME_PATTERN = new RegExp(`^${MCP_SERVER_NAME_SOURCE}$`);

/** A header the server expects: `<headerName>: <headerPrefix> <value>`, or the bare value without a prefix. */
export type McpServerApiKeyInput = {
  headerName: string;
  headerPrefix: string;
  value: string;
};

/**
 * A client registered with the server by hand, for servers without dynamic
 * client registration (Slack, Asana), or whose registration endpoint is not
 * on their issuer's origin.
 */
export type McpServerOAuthClientInput = {
  clientId: string;
  clientSecret: string | null;
};

export type McpServerAuthInput =
  | { type: typeof MCP_SERVER_AUTH_TYPES.NONE }
  | { type: typeof MCP_SERVER_AUTH_TYPES.API_KEY; apiKey: McpServerApiKeyInput }
  | {
      type: typeof MCP_SERVER_AUTH_TYPES.OAUTH;
      client: McpServerOAuthClientInput | null;
      /**
       * The scopes cubic asks for, space-separated. Null asks for every scope
       * the server advertises.
       */
      scope: string | null;
    };

/** How cubic registered itself as an OAuth client with the server. */
export const MCP_SERVER_OAUTH_REGISTRATIONS = {
  DYNAMIC: 'dynamic',
  STATIC: 'static',
} as const satisfies Record<string, DbMcpServerOAuthRegistration>;

export type McpServerOAuthRegistration =
  (typeof MCP_SERVER_OAUTH_REGISTRATIONS)[keyof typeof MCP_SERVER_OAUTH_REGISTRATIONS];

/** An account on an OAuth server: the viewing user's own, or the shared one. */
export const MCP_SERVER_OAUTH_STATUSES = {
  CONNECTED: 'connected',
  /** Connected once, but the token was revoked or can no longer be refreshed. */
  NEEDS_AUTH: 'needs_auth',
  NOT_CONNECTED: 'not_connected',
} as const;

export type McpServerOAuthStatus =
  (typeof MCP_SERVER_OAUTH_STATUSES)[keyof typeof MCP_SERVER_OAUTH_STATUSES];

/**
 * Whose account an OAuth server's token belongs to: a user's own, or the one
 * an admin connected for everyone. A user's own working account wins.
 */
export const MCP_SERVER_ACCOUNTS = {
  PERSONAL: 'personal',
  SHARED: 'shared',
} as const;

export type McpServerAccount = (typeof MCP_SERVER_ACCOUNTS)[keyof typeof MCP_SERVER_ACCOUNTS];

/** The account an admin connected to an OAuth server for everyone. */
export type McpServerSharedAccountView = {
  status: Exclude<McpServerOAuthStatus, typeof MCP_SERVER_OAUTH_STATUSES.NOT_CONNECTED>;
  /** The admin who last signed it in, who may have left the org since. */
  connectedBy: string;
  connectedAt: string;
};

/** What the settings API returns for a server; never includes a secret. */
export type McpServerView = {
  id: string;
  name: string;
  /** Only the origin for a viewer without write access: some URLs carry a secret in the path. */
  url: string;
  disabledTools: string[];
  createdAt: string;
  updatedAt: string;
} & (
  | { authType: typeof MCP_SERVER_AUTH_TYPES.NONE }
  | {
      authType: typeof MCP_SERVER_AUTH_TYPES.API_KEY;
      apiKey: { headerName: string; headerPrefix: string };
    }
  | {
      authType: typeof MCP_SERVER_AUTH_TYPES.OAUTH;
      oauth: {
        /** The viewing user's own account. */
        status: McpServerOAuthStatus;
        /** The account an admin connected for everyone; null when there is none. */
        shared: McpServerSharedAccountView | null;
      };
      /** The scopes cubic asks for; null asks for every scope the server advertises. */
      scope: string | null;
    }
);

export type McpServerTool = {
  name: string;
  description: string | null;
};

/** Why cubic could not list a server's tools. */
export const MCP_SERVER_CONNECTION_FAILURES = {
  /** The URL is not HTTPS, carries credentials, or resolves to a private or reserved address. */
  UNSAFE_URL: 'unsafe_url',
  UNREACHABLE: 'unreachable',
  /** The server answered 401/403 to the credentials sent (or to none). */
  UNAUTHORIZED: 'unauthorized',
  /** The server signs in with OAuth, but another auth type was chosen. */
  OAUTH_REQUIRED: 'oauth_required',
  /** The server answered, but not as an MCP server. */
  NOT_MCP: 'not_mcp',
  TIMEOUT: 'timeout',
  /** OAuth was chosen, but the server does not sign in with OAuth or cubic cannot register as its client. */
  OAUTH_UNAVAILABLE: 'oauth_unavailable',
} as const;

export type McpServerConnectionFailure =
  (typeof MCP_SERVER_CONNECTION_FAILURES)[keyof typeof MCP_SERVER_CONNECTION_FAILURES];

export type McpServerConnectionResult<TTool> =
  | { ok: true; tools: TTool[] }
  | { ok: false; failure: McpServerConnectionFailure; message: string };

// Settings API contracts: /api/settings/mcp-servers/[installationId]/…

export type ListMcpServersResponse = {
  servers: McpServerView[];
  /** Whether the viewer may add, change and remove servers: admins only. */
  canEdit: boolean;
  /** Whether the viewer may connect and disconnect shared accounts: an admin, not impersonating. */
  canManageSharedAccounts: boolean;
};

export type CreateMcpServerRequest = {
  /** Picked by the add form, since an OAuth server's redirect URI names it before the server is saved. */
  id: string;
  name: string;
  url: string;
  auth: McpServerAuthInput;
};

/** Creation connects first; a server whose tools cannot be listed is not saved. */
export type CreateMcpServerResponse =
  | { ok: true; server: McpServerView }
  | { ok: false; failure: McpServerConnectionFailure; message: string };

/** What cubic connects to: a server's URL and the auth to send it. */
export type McpServerConnectionInput = Omit<CreateMcpServerRequest, 'id' | 'name'>;

/** How a server signs in, as cubic found out by connecting without credentials. */
export type McpServerDetectedAuth =
  | { type: typeof MCP_SERVER_AUTH_TYPES.NONE; toolCount: number }
  | { type: typeof MCP_SERVER_AUTH_TYPES.API_KEY }
  | {
      type: typeof MCP_SERVER_AUTH_TYPES.OAUTH;
      /** cubic can't register a client of its own; the admin enters one. */
      needsClient: boolean;
    };

export type DetectMcpServerRequest = { url: string };

/** A server cubic cannot reach or read is a 200 with `ok: false`. */
export type DetectMcpServerResponse =
  | { ok: true; auth: McpServerDetectedAuth }
  | { ok: false; failure: McpServerConnectionFailure; message: string };

/** Each tool is on unless its name is in the server's `disabledTools`. */
export type ListMcpServerToolsResponse = McpServerConnectionResult<McpServerTool>;

/** Either field may be sent alone. */
export type UpdateMcpServerRequest = {
  disabledTools?: string[];
  apiKey?: McpServerApiKeyInput;
};

export type UpdateMcpServerResponse = { server: McpServerView };

export type McpServersApiError = { error: string };

// OAuth: POST …/[serverId]/oauth starts the viewing user's connection, DELETE
// …/[serverId]/oauth disconnects it. …/[serverId]/oauth/shared does the same
// for the shared account, for admins only. The provider redirects to the
// server's own callback, /api/mcp-servers/oauth/callback/[serverId], which
// redirects back to the settings page with `MCP_SERVER_OAUTH_RESULT_PARAM` set
// to one of the results below.

export type StartMcpServerOAuthResponse = { authorizationUrl: string };

/**
 * Where cubic's redirect URIs live, on its own origin. Each server gets its
 * own, `<path>/<serverId>`, so a client registered for one server answers to
 * no other server's provider: a code always comes back to the callback of the
 * server the client was registered for, which refuses it when the attempt was
 * for another server.
 */
export const MCP_SERVER_OAUTH_CALLBACK_PATH = '/api/mcp-servers/oauth/callback';

/** A server's redirect URI in full: what cubic registers for it, and what the settings form shows an admin. */
export function mcpServerOAuthRedirectUrl(serverId: string): string {
  return new URL(`${MCP_SERVER_OAUTH_CALLBACK_PATH}/${serverId}`, baseUrl()).href;
}

/** The settings subpage that lists MCP servers, which the OAuth callback returns to. */
export const MCP_SERVERS_SETTINGS_INTEGRATION = INTEGRATION_TYPES.MCP_SERVERS;

export const MCP_SERVER_OAUTH_RESULT_PARAM = 'mcpServerOAuth';

export const MCP_SERVER_OAUTH_RESULTS = {
  CONNECTED: 'connected',
  CONNECTED_SHARED: 'connected_shared',
  /** The user declined on the provider's consent screen. */
  DENIED: 'denied',
  /** The attempt was too old, already used, or started by someone else. */
  EXPIRED: 'expired',
  FAILED: 'failed',
} as const;

export type McpServerOAuthResult =
  (typeof MCP_SERVER_OAUTH_RESULTS)[keyof typeof MCP_SERVER_OAUTH_RESULTS];
